import { useQuery } from "@tanstack/react-query";
import { collection, getDocs, getFirestore } from "firebase/firestore";
import { useCallback, useState } from "react";
import { queryKeys } from "../../../queries/queryKeys";
import { actualizarCuotas } from "../../../utils/actualizarCuotas";
import { procesarClientesPagadosDeRuta } from "../../../utils/clientesPagados";
import type {
  ClienteRuta,
  UseVerDetallesRutasModalReturn,
  VerDetallesRutasModalProps,
} from "./types";

type VistaActiva = "clientes" | "estadisticas" | "gastos" | null;

const obtenerClientesRuta = async (adminUid: string, rutaUid: string): Promise<ClienteRuta[]> => {
  const db = getFirestore();
  const querySnapshot = await getDocs(
    collection(db, "admin_users", adminUid, "rutas", rutaUid, "clientes"),
  );

  return querySnapshot.docs.map((docSnapshot) => ({
    uid: docSnapshot.id,
    ...docSnapshot.data(),
  }));
};

const fetchClientesDetalleRuta = async (
  adminUid: string,
  rutaUid: string,
): Promise<ClienteRuta[] | null> => {
  try {
    const data = await obtenerClientesRuta(adminUid, rutaUid);
    const clientesActualizados = await actualizarCuotas({
      infoClientes: data,
      usuarioRuta: {
        adminUid,
        uid: rutaUid,
      },
    });
    const { clientesActivos } = await procesarClientesPagadosDeRuta({
      adminUid,
      clientes: (clientesActualizados ?? data) as ClienteRuta[],
      fechaReferencia: new Date(),
      rutaUid,
    });

    return clientesActivos;
  } catch (error) {
    console.error("Error fetching data from Firebase:", error);
    return null;
  }
};

export const useVerDetallesRutasModal = ({
  selectedRuta,
  setVerDetalles,
}: VerDetallesRutasModalProps): UseVerDetallesRutasModalReturn | null => {
  const [vistaActiva, setVistaActiva] = useState<VistaActiva>(null);
  const adminUid = selectedRuta?.adminUid ?? undefined;
  const rutaUid = selectedRuta?.uid ?? undefined;

  // Vista de solo lectura abierta bajo demanda (por fila clickeada); reabrir
  // el modal para la misma ruta ya vista en la sesion sirve desde cache en
  // vez de repetir el fetch + recalculo de cuotas + auto-archivado.
  const { data: clientes = null } = useQuery({
    enabled: Boolean(adminUid && rutaUid),
    gcTime: 10 * 60 * 1000,
    queryFn: () => fetchClientesDetalleRuta(adminUid as string, rutaUid as string),
    queryKey: queryKeys.clientesDetalleRuta(adminUid, rutaUid),
    staleTime: 2 * 60 * 1000,
  });

  const handleClose = useCallback(() => {
    setVerDetalles(false);
  }, [setVerDetalles]);

  const handleOpenVerClientes = useCallback(() => {
    setVistaActiva("clientes");
  }, []);

  const handleCloseVerClientes = useCallback(() => {
    setVistaActiva(null);
  }, []);

  const handleOpenGastos = useCallback(() => {
    setVistaActiva("gastos");
  }, []);

  const handleCloseGastos = useCallback(() => {
    setVistaActiva(null);
  }, []);

  const handleOpenEstadisticas = useCallback(() => {
    setVistaActiva("estadisticas");
  }, []);

  const handleCloseEstadisticas = useCallback(() => {
    setVistaActiva(null);
  }, []);

  if (!selectedRuta) {
    return null;
  }

  return {
    clientes,
    handleClose,
    handleCloseEstadisticas,
    handleCloseGastos,
    handleCloseVerClientes,
    handleOpenEstadisticas,
    handleOpenGastos,
    handleOpenVerClientes,
    selectedRuta,
    verClientes: vistaActiva === "clientes",
    verEstadisticas: vistaActiva === "estadisticas",
    verGastos: vistaActiva === "gastos",
  };
};
